'use client';

import React, { useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CareerPage from '../pages/CareerPage';
import { motion, AnimatePresence } from 'framer-motion';

const TeamMemberCard = ({ member, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      layout
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.2, layout: { duration: 0.4, ease: "easeInOut" } }}
      className="bg-white flex flex-col md:flex-row shadow-sm hover:shadow-lg transition-shadow duration-500 overflow-hidden h-full"
    >
      {/* Image */}
      <motion.div layout className="w-full md:w-[40%] shrink-0 relative overflow-hidden aspect-[3/4]">
        <img
          src={member.image}
          alt={member.name}
          className={`absolute inset-0 w-full h-full object-cover transition duration-700 md:group-hover:grayscale-0 ${isHovered ? 'md:grayscale-0' : 'md:grayscale'}`}
        />
      </motion.div>

      {/* Text Container */}
      <motion.div
        layout
        className={`w-full md:w-[60%] p-8 lg:p-12 flex flex-col justify-center h-full transition-all duration-500 md:items-center md:text-center ${isHovered ? 'md:!items-start md:!text-left' : ''} items-center text-center`}
      >
        <motion.h3 layout className="text-lg lg:text-xl font-bold uppercase tracking-widest text-black mb-1">
          {member.name}
        </motion.h3>

        {/* Desktop Animated Role/Desc */}
        <div className="hidden md:block w-full">
          <AnimatePresence mode="wait">
            {!isHovered ? (
              <motion.p
                key="role"
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: 0.1 } }}
                exit={{ opacity: 0, transition: { duration: 0.2 } }}
                className="text-gray-500 text-sm font-medium mb-5 text-center"
              >
                {member.role}
              </motion.p>
            ) : (
              <motion.p
                key="desc"
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: 0.1 } }}
                exit={{ opacity: 0, transition: { duration: 0.2 } }}
                className="text-gray-700 text-sm lg:text-base leading-relaxed my-4 text-left"
              >
                {member.description}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        {/* Mobile Static Role/Desc */}
        <div className="md:hidden flex flex-col items-center text-center w-full">
          <p className="text-gray-500 text-sm font-medium mb-3">
            {member.role}
          </p>
          {member.description && (
            <p className="text-gray-700 text-sm leading-relaxed mb-4">
              {member.description}
            </p>
          )}
        </div>

        {member.linkedin && (
          <motion.a
            layout
            href={member.linkedin}
            target="_blank"
            rel="noreferrer"
            className="bg-black text-white text-[10px] lg:text-xs font-bold px-6 py-2 tracking-[0.2em] uppercase hover:bg-gray-800 transition-colors mt-2"
          >
            LINKEDIN
          </motion.a>
        )}
      </motion.div>
    </motion.div>
  );
};

const StudioPage = () => {
  const heroImage = `${process.env.PUBLIC_URL}/images/Hero/studiohero.webp`;

  const teamMembers = [
    {
      name: 'Ved Prakash Mehta',
      role: 'Sr Founder, Civil Engineer',
      image: '/images/team/mehta.webp',
      description: 'Ved Prakash Mehta brings decades of engineering excellence, laying the strong foundation of our practice with a keen eye on structural integrity and sustainable building practices.',
    },
    {
      name: 'Sandeep Mehta',
      role: 'Sr Founder, Architect',
      image: '/images/team/srmehta.webp',
      description: 'Sandeep Mehta is the visionary force behind the architectural language of the studio, blending timeless design principles with modern innovation to create spaces that resonate with context.',
    },
    {
      name: 'Aditya Mehta',
      role: 'Principal Architect',
      image: '/images/team/aditya-mehta.png',
      description: 'Aditya Mehta leads the design direction, focusing on creating immersive spatial experiences through a careful selection of materials, light, and contextually responsive architecture.',
      linkedin: 'https://www.linkedin.com/in/aditya-mehta-66618625b',
    },
    {
      name: 'Jahanzaib Bhat',
      role: 'Project Architect',
      image: `/images/team/jahanzaib.jpg`,
      description: 'Jahanzaib Bhat meticulously translates design concepts into reality, overseeing project execution with extreme attention to detail and a commitment to high-quality craftsmanship.',
      linkedin: 'https://linkedin.com/in/jahanzaib-bhat',
    },
  ];


  const studioImages = useMemo(
    () => [
      `${process.env.PUBLIC_URL}/images/studio/studio1.webp`,
      `${process.env.PUBLIC_URL}/images/studio/studio2.webp`,
      `${process.env.PUBLIC_URL}/images/studio/studio3.webp`,
      `${process.env.PUBLIC_URL}/images/studio/studio4.webp`,
      `${process.env.PUBLIC_URL}/images/studio/studio5.webp`,
      `${process.env.PUBLIC_URL}/images/studio/studio6.webp`,
    ],
    []
  );

  return (
    <div className="bg-white text-black min-h-screen font-sans">
      <Navbar />

      {/* Hero Section */}
      <section
        id="hero"
        className="relative h-[60vh] flex flex-col justify-center items-center overflow-hidden bg-black"
      >
        <img
          src={heroImage}
          alt="Studio Hero"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />

        <motion.div
          className="relative z-10 text-center px-6 text-white"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut' }}
        >
          <h1 className="max-w-[92vw] text-center text-[clamp(2.5rem,10vw,7rem)] leading-[0.95] break-words font-heading font-light mb-4 tracking-wide">
            STUDIO
          </h1>
        </motion.div>
      </section>



      {/* About */}
      <section className="py-24 px-8 md:px-20 bg-white text-gray-800">
        <motion.h2
          className="text-3xl md:text-5xl font-light mb-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          About the Studio
        </motion.h2>

        <motion.div
          className="grid md:grid-cols-2 gap-12 text-lg text-gray-600"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <p>
            Founded with a focus on contextual and sustainable design, Studio Sharan
            explores architecture through materials, light, and spatial experience.
          </p>
          <p>
            The practice engages with residential, hospitality, and public projects,
            grounded in simplicity, proportion, and local identity.
          </p>
        </motion.div>
      </section>

      {/* TEAM SECTION */}
      <section className="py-24 px-8 md:px-20 bg-gray-50">
        <motion.h2
          className="text-3xl md:text-5xl font-light mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Our Team
        </motion.h2>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {teamMembers.map((member, index) => (
            <TeamMemberCard key={index} member={member} index={index} />
          ))}
        </div>
      </section>

      {/* Studio Gallery */}
      <section className="py-24 px-8 md:px-20 bg-black">
        <motion.h2
          className="text-3xl md:text-5xl font-light mb-14 text-white"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Inside the Studio
        </motion.h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {studioImages.map((src, index) => (
            <motion.div
              key={index}
              className="relative aspect-video overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <img
                src={src}
                alt={`Studio ${index + 1}`}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-[1.04]"
              />
            </motion.div>
          ))}
        </div>
      </section>

      <CareerPage />
      <Footer />
    </div>
  );
};

export default StudioPage;
